import { Component, EventEmitter, Input, OnChanges, Output, ViewEncapsulation } from '@angular/core';
import { TranslateService } from 'ng2-translate';
import StringUtils from '../../../../utils/string-utils';
import { DialogType } from '../../../domain/enums/dialog-type';
import { DistributionStatus } from '../../../domain/enums/distribution-status';
import { DialogProperties, Registration } from '../../../domain/models';
import { Device } from '../../../domain/models/distribution/device';
import { DeviceViewModel } from '../../view-models/device.view-model';
import { DistributionViewModel } from '../../view-models/distribution.view-model';
import { StopDistributionReasonDto } from '../../../domain/models/stop-distribution-reason-dto';
import { Observable } from 'rxjs';
import { DistributionTiming } from '../../../domain/enums/distribution-timing';
import { State } from '@progress/kendo-data-query';
import { ConfigService, ArrayType } from '../../../../core/config.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import * as _ from 'lodash';
import { DeviceConfiguration } from '../../view-models/device-configuration';
import { StreetItemViewModel } from '../../view-models/street-item.view-model';

@Component({
    selector: 'app-detail',
    templateUrl: './detail.component.html',
    styleUrls: ['./detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DetailComponent implements OnChanges {

    @Input() detail: DistributionViewModel;
    @Input() deviceId: string;
    @Input() filteredStreets: StreetItemViewModel[];
    @Input() deviceConfigurations: DeviceConfiguration[];
    @Input() localShowAllDevices: boolean;
    @Input() localShowDistribution: boolean;
    @Input() localShowIcons: boolean;

    @Output() goToOverview: EventEmitter<null> = new EventEmitter();
    @Output() setDeviceSelected: EventEmitter<string> = new EventEmitter();
    @Output() startDistribution: EventEmitter<any> = new EventEmitter();
    @Output() stopDistribution: EventEmitter<StopDistributionReasonDto> = new EventEmitter();
    @Output() showEarlyLate: EventEmitter<boolean> = new EventEmitter();
    @Output() showAllDevices: EventEmitter<boolean> = new EventEmitter();
    @Output() showIcons: EventEmitter<boolean> = new EventEmitter();
    @Output() showDistribution: EventEmitter<boolean> = new EventEmitter();
    @Output() toggleDeviceChanged: EventEmitter<any> = new EventEmitter();
    @Output() filterStreets: EventEmitter<DistributionTiming> = new EventEmitter();
    @Output() setInSearchMode: EventEmitter<void> = new EventEmitter();

    public confirmDialog: DialogProperties;
    public toggleConfirmDialog: boolean;
    public toggleReasonDialog: boolean;
    public currentReason: Observable<string>;
    public distributionTimingFilterOptions: any;
    public activeDistributionTimingFilterOption: any;
    private defaultPageSize: number;
    public pageSizes: number[];
    public state: State;
    public showAll: boolean;
    public showAllSpeeds: boolean;

    constructor(
        private translate: TranslateService,
        private configService: ConfigService
    ) {
        this.defaultPageSize = parseInt(this.configService.getEnv('gridDefaultPageSize'), 10);
        this.pageSizes = this.configService.getArray('gridPossiblePageSizes', ArrayType.Number);
        this.state = {
            skip: 0,
            take: this.defaultPageSize
        };
        this.distributionTimingFilterOptions = [
            { value: null, displayValue: 'SHOW_ALL' },
            { value: DistributionTiming.Early, displayValue: 'DISTRIBUTION_TYPE_EARLY' },
            { value: DistributionTiming.OnTime, displayValue: 'DISTRIBUTION_TYPE_ON_TIME' },
            { value: DistributionTiming.Late, displayValue: 'DISTRIBUTION_TYPE_LATE' }
        ];
        this.activeDistributionTimingFilterOption = this.distributionTimingFilterOptions[0];
        this.showAll = true;
        this.showAllSpeeds = false;
    }

    public getTranslationTiming(distributionTiming: DistributionTiming): string {
        switch (distributionTiming) {
            case DistributionTiming.Early: return 'DISTRIBUTION_TYPE_EARLY';
            case DistributionTiming.OnTime: return 'DISTRIBUTION_TYPE_ON_TIME';
            case DistributionTiming.Late: return 'DISTRIBUTION_TYPE_LATE';
            case DistributionTiming.None: return 'DISTRIBUTION_TYPE_NONE';
            default: return 'TIMING_UNKNOWN';
        }
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        this.state = state;
    }

    ngOnChanges(): void {
        if (!this.detail) {
            return;
        }
        this.confirmDialog = new DialogProperties({
            type: DialogType.ConfirmDialog,
            title: this.detail.status === DistributionStatus.Completed ? 'CONFIRM_CYCLE_START' : 'CONFIRM_CYCLE_STOP',
            additionalInfo: 'CONFIRM_ADDITIONAL_INFO'
        });
    }

    public getDistributionProgress(): number {
        return this.detail ? this.detail.distributionProgress + this.detail.distributionProgressEarly + this.detail.distributionProgressLate : 0;
    }
    public getDeviceColor(device: string): string {
        let configuration: DeviceConfiguration = this.deviceConfigurations.find((d: DeviceConfiguration) => d.deviceId === device);
        if (configuration === undefined || configuration === null) { return 'black'; }
        let color: string = configuration.deviceColor;
        return color !== undefined ? color : 'black';
    }

    public getDeviceDistributionProgress(deviceVM: DeviceViewModel): number {
        return deviceVM.distributionProgress + deviceVM.distributionProgressEarly + deviceVM.distributionProgressLate;
    }

    get selectedDevice(): DeviceViewModel {
        return this.detail ? this.detail.devices.find((device: DeviceViewModel) => device.id === this.deviceId) : null;
    }

    public batteryEmptyOrUnknown(battery: number): boolean {
        return battery === null || battery === undefined || battery < 10;
    }

    public batteryQuarter(battery: number): boolean {
        return battery >= 10 && battery < 40;
    }

    public batteryHalf(battery: number): boolean {
        return battery >= 40 && battery < 60;
    }

    public batteryThreeQuarters(battery: number): boolean {
        return battery >= 60 && battery < 90;
    }

    public batteryFull(battery: number): boolean {
        return battery >= 90;
    }

    public getDeviceDistributionTime(device: Device): string {
        if (!device.registrations || device.registrations.length === 0) {
            return '';
        }
        return StringUtils.getDayDateFormat(device.registrations[0].startTime, this.translate.currentLang);
    }
    public isChecked(deviceId: string): boolean {
        if (this.deviceConfigurations === undefined || this.deviceConfigurations.length === 0) {
            return false;
        }
        return this.deviceConfigurations.find((d: DeviceConfiguration) => d.deviceId === deviceId).toggled;
    }

    public getBackgroundClass(device: DeviceViewModel): any {
        return this.deviceId && device.id === this.deviceId ? 'device-selected' : 'device-not-selected';
    }

    public deviceSelected(device: DeviceViewModel): void {
        this.setDeviceSelected.emit(device.id);
    }

    public getRegistrationEnd(registration: Registration): string {
        return StringUtils.getDateTimeFormat(registration.endTime);
    }

    public getRegistrationStart(registration: Registration): string {
        return StringUtils.getDateTimeFormat(registration.startTime);
    }

    public isCycleStopped(): boolean {
        return this.detail ? this.detail.status === DistributionStatus.Completed : false;
    }

    public isCycleStarted(): boolean {
        return this.detail ? this.detail.status === DistributionStatus.Registering : false;
    }

    public showConfirmDialog(): void {
        this.toggleConfirmDialog = true;
    }

    public closeConfirmDialog(action: string): void {
        if (action === 'confirm') {
            this.startDistribution.emit(this.detail.id);
        }
        this.toggleConfirmDialog = false;
    }

    public closeReasonDialog(reason: string): void {
        if (reason !== null) {
            this.stopDistribution.emit(new StopDistributionReasonDto(this.detail.id, reason));
        }
        this.toggleReasonDialog = false;
    }

    public showReasonDialog(): void {
        this.toggleReasonDialog = true;
    }

    public getDurationChar(): string {
        return this.translate.instant('TIMECHAR');
    }

    public backToOverview(): void {
        this.goToOverview.emit();
    }

    public getCompletionClass(progress: number): string {
        if (progress === 0) {
            return 'no-completion';
        } else if (progress < 50) {
            return 'small-completion';
        } else if (progress < 90) {
            return 'almost-completed';
        } else {
            return 'completed';
        }
    }

    public reasonDialogIsToggled(): boolean {
        return this.toggleReasonDialog;
    }

    public confirmDialogIsToggled(): boolean {
        return this.toggleConfirmDialog;
    }

    public onSwitchChange(toggle: boolean): void {
        this.showEarlyLate.emit(toggle);
    }

    public onDistributionTimingFilterChange(selectedOption: any): void {
        this.filterStreets.emit(selectedOption.value);
    }
    public toggledShowAllDevices(event: boolean): void {
        this.showAllDevices.emit(event);
    }
    public toggledShowIcons(event: boolean): void {
        this.showIcons.emit(event);
    }
    public toggledShowDistribution(event: boolean): void {
        this.showDistribution.emit(event);
    }
    public toggledDeviceChanged(event: boolean, id: string): void {
        this.toggleDeviceChanged.emit({ id: id, value: event });
    }
    public onSetInSearchMode(): void {
        this.setInSearchMode.emit();
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/distribution/presentation/detail/detail.component.ts